import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { adminGetTenantJobs, adminResetPassword, adminCreateTenantDiscount } from '../../lib/adminApi';
import AdminSidebar from '../../components/AdminSidebar';

const NAVY   = '#0a2540';
const ORANGE = '#F37428';
const BLUE   = '#0176CE';

function fmt$(n) { return n == null ? '—' : '$' + Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }); }
function fmtDate(raw) { if (!raw) return '—'; return new Date(raw).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); }

function StatusBadge({ status }) {
  const cfg = {
    complete:   { color: '#059669', bg: 'rgba(5,150,105,0.08)'   },
    processing: { color: BLUE,      bg: 'rgba(1,118,206,0.08)'   },
    pending:    { color: '#b45309', bg: 'rgba(245,158,11,0.08)'  },
    failed:     { color: '#dc2626', bg: 'rgba(239,68,68,0.08)'   },
    uploaded:   { color: '#64748b', bg: 'rgba(100,116,139,0.08)' },
    active:     { color: '#059669', bg: 'rgba(5,150,105,0.08)'   },
    inactive:   { color: '#94a3b8', bg: 'rgba(148,163,184,0.1)'  },
  };
  const c = cfg[status?.toLowerCase()] || cfg.uploaded;
  return <span style={{ background: c.bg, color: c.color, borderRadius: 5, fontSize: 10, fontWeight: 600, padding: '2px 8px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{status || '—'}</span>;
}

function ResetPasswordModal({ user, onClose }) {
  const [pw, setPw]           = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone]       = useState(false);
  const [error, setError]     = useState('');

  async function handleSubmit(e) {
    e.preventDefault();
    if (pw.length < 8) { setError('Minimum 8 characters'); return; }
    setLoading(true); setError('');
    try {
      await adminResetPassword(user.user_id, pw);
      setDone(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={s.overlay}>
      <div style={s.modal}>
        <h2 style={s.modalTitle}>Reset Password</h2>
        <p style={{ color: '#64748b', fontSize: 13, margin: '0 0 20px' }}>
          Set a new password for <strong>{user.full_name || user.email}</strong>
        </p>
        {done ? (
          <>
            <div style={s.successBox}>Password updated successfully.</div>
            <button style={s.btnPrimary} onClick={onClose}>Done</button>
          </>
        ) : (
          <form onSubmit={handleSubmit}>
            {error && <div style={s.errorBox}>{error}</div>}
            <input style={s.input} type="password" placeholder="New password (min 8 chars)"
              value={pw} onChange={e => setPw(e.target.value)} required autoFocus />
            <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
              <button type="submit" style={s.btnPrimary} disabled={loading}>{loading ? 'Saving…' : 'Update Password'}</button>
              <button type="button" style={s.btnGhost} onClick={onClose}>Cancel</button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

export default function AdminTenantDetail() {
  const { tenantId } = useParams();
  const navigate     = useNavigate();

  const [data, setData]         = useState(null);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');
  const [tab, setTab]           = useState('jobs');
  const [resetUser, setResetUser] = useState(null);
  const [sortCol, setSortCol]   = useState('created_on_utc');
  const [sortDir, setSortDir]   = useState('desc');

  function toggleSort(col) {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('asc'); }
  }

  // Discount form
  const [disc, setDisc]         = useState({ code: '', type: 'percent', value: '', max_uses: '', expires: '', notes: '' });
  const [discSaving, setDiscSaving] = useState(false);
  const [discError, setDiscError]   = useState('');
  const [discSaved, setDiscSaved]   = useState(false);

  useEffect(() => {
    adminGetTenantJobs(tenantId)
      .then(res => {
        setData(res);
        setLoading(false);
        // Auto-suggest discount code from company name
        const cname = res?.tenant?.company_name || '';
        const slug  = cname.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 10);
        setDisc(prev => ({ ...prev, code: slug ? `${slug}-10` : '' }));
      })
      .catch(e => { setError(e.message); setLoading(false); });
  }, [tenantId]);

  async function handleDiscountSubmit(e) {
    e.preventDefault();
    setDiscError(''); setDiscSaving(true); setDiscSaved(false);
    try {
      const payload = {
        code:           disc.code,
        discount_type:  disc.type,
        discount_value: Number(disc.value),
      };
      if (disc.max_uses) payload.max_uses = Number(disc.max_uses);
      if (disc.expires)  payload.expires_on = disc.expires;
      if (disc.notes)    payload.notes = disc.notes;
      await adminCreateTenantDiscount(tenantId, payload);
      setDiscSaved(true);
      setTimeout(() => setDiscSaved(false), 4000);
    } catch (err) {
      setDiscError(err.message);
    } finally {
      setDiscSaving(false);
    }
  }

  const tenant = data?.tenant || {};
  const users  = data?.users  || [];
  const jobs   = (data?.jobs || []).slice().sort((a, b) => {
    let av = a[sortCol] ?? '', bv = b[sortCol] ?? '';
    if (typeof av === 'number' || typeof bv === 'number') { av = Number(av||0); bv = Number(bv||0); }
    else { av = String(av).toLowerCase(); bv = String(bv).toLowerCase(); }
    if (av < bv) return sortDir === 'asc' ? -1 : 1;
    if (av > bv) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });

  const totalRecords  = jobs.reduce((sum, j) => sum + (j.record_count    || 0), 0);
  const totalEnriched = jobs.reduce((sum, j) => sum + (j.processed_count || 0), 0);

  const TABS = ['Jobs', 'Team', 'Create Discount'];

  return (
    <div style={s.page}>
      <AdminSidebar active="tenants" />
      <main style={s.main}>
        {/* Header */}
        <div style={s.header}>
          <button style={s.backBtn} onClick={() => navigate('/admin/tenants')}>← Back to Tenants</button>
          {!loading && !error && (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <h1 style={s.title}>{tenant.company_name || '…'}</h1>
                <StatusBadge status={tenant.status} />
                <span style={{ background: 'rgba(1,118,206,0.08)', color: BLUE, borderRadius: 5, fontSize: 11, fontWeight: 600, padding: '2px 10px', textTransform: 'capitalize' }}>
                  {tenant.plan || 'free'} plan
                </span>
              </div>
              <p style={s.sub}>{tenant.email}</p>
            </>
          )}
        </div>

        {loading && <div style={{ color: '#64748b', padding: 32 }}>Loading…</div>}
        {error   && <div style={{ color: '#ef4444', padding: 32 }}>{error}</div>}

        {!loading && !error && (
          <>
            {/* Stat cards */}
            <div style={s.statsRow}>
              {[
                { label: 'Total Revenue',     value: fmt$(tenant.total_spent),              color: BLUE   },
                { label: 'Total Jobs',         value: jobs.length,                           color: ORANGE },
                { label: 'Records Uploaded',   value: totalRecords.toLocaleString(),         color: BLUE   },
                { label: 'Records Enriched',   value: totalEnriched.toLocaleString(),        color: ORANGE },
              ].map(({ label, value, color }) => (
                <div key={label} style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, borderTop: `4px solid ${color}`, padding: '20px 24px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                  <div style={{ fontSize: 26, fontWeight: 700, color, letterSpacing: '-0.5px', marginBottom: 2 }}>{value}</div>
                  <div style={{ color: '#64748b', fontSize: 12, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</div>
                </div>
              ))}
            </div>

            {/* Tab bar */}
            <div style={s.tabBar}>
              {TABS.map(t => (
                <button
                  key={t}
                  onClick={() => setTab(t.toLowerCase().replace(' ', '-'))}
                  style={{
                    ...s.tabBtn,
                    borderBottom: tab === t.toLowerCase().replace(' ', '-') ? `2px solid ${ORANGE}` : '2px solid transparent',
                    color:        tab === t.toLowerCase().replace(' ', '-') ? NAVY : '#64748b',
                    fontWeight:   tab === t.toLowerCase().replace(' ', '-') ? 600 : 400,
                  }}
                >{t}</button>
              ))}
            </div>

            {/* Jobs tab */}
            {tab === 'jobs' && (
              <div style={s.card}>
                <table style={s.table}>
                  <thead>
                    <tr>
                      {[
                        { label: 'File',      col: 'file_name'       },
                        { label: 'Records',   col: 'record_count'    },
                        { label: 'Enriched',  col: 'processed_count' },
                        { label: 'Status',    col: 'status'          },
                        { label: 'Amount',    col: 'amount_usd'      },
                        { label: 'Submitted', col: 'created_on_utc'  },
                        { label: 'Downloads', col: null              },
                      ].map(({ label, col }) => (
                        <th key={label} style={{ ...s.th, cursor: col ? 'pointer' : 'default', userSelect: 'none', whiteSpace: 'nowrap' }}
                          onClick={() => col && toggleSort(col)}>
                          {label} {col && (sortCol === col ? (sortDir === 'asc' ? '↑' : '↓') : <span style={{ color: '#d1d5db' }}>↕</span>)}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {jobs.map(j => (
                      <tr key={j.job_id} style={{ ...s.tr, cursor: 'pointer' }}>
                        <td style={{ ...s.td, color: '#64748b', fontSize: 12, maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          <a href={`/admin/tenants/${tenantId}/jobs/${j.job_id}/records`} style={{ color: BLUE, textDecoration: 'none', fontWeight: 500 }} title="View records">
                            {j.file_name}
                          </a>
                        </td>
                        <td style={s.td}>{(j.record_count || 0).toLocaleString()}</td>
                        <td style={s.td}>{(j.processed_count || 0).toLocaleString()}</td>
                        <td style={s.td}><StatusBadge status={j.status} /></td>
                        <td style={{ ...s.td, color: j.amount_usd ? '#059669' : '#94a3b8', fontWeight: j.amount_usd ? 600 : 400 }}>{j.amount_usd ? fmt$(j.amount_usd) : '—'}</td>
                        <td style={{ ...s.td, color: '#94a3b8', fontSize: 12 }}>{fmtDate(j.created_on_utc)}</td>
                        <td style={{ ...s.td }}>
                          <div style={{ display: 'flex', gap: 6 }}>
                            {j.blob_input_url ? (
                              <a href={j.blob_input_url} target="_blank" rel="noreferrer"
                                style={s.dlBtn}
                                onClick={e => e.stopPropagation()}>↓ Input</a>
                            ) : (
                              <span style={{ ...s.dlBtn, opacity: 0.4, cursor: 'default', pointerEvents: 'none' }}>↓ Input</span>
                            )}
                            {j.blob_output_url ? (
                              <a href={j.blob_output_url} target="_blank" rel="noreferrer"
                                style={{ ...s.dlBtn, borderColor: ORANGE, color: ORANGE }}
                                onClick={e => e.stopPropagation()}>↓ Results</a>
                            ) : (
                              <span style={{ ...s.dlBtn, borderColor: ORANGE, color: ORANGE, opacity: 0.4, cursor: 'default', pointerEvents: 'none' }}>↓ Results</span>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                    {jobs.length === 0 && (
                      <tr><td colSpan={7} style={{ ...s.td, color: '#94a3b8', textAlign: 'center', padding: 32 }}>No jobs yet</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}

            {/* Team tab */}
            {tab === 'team' && (
              <div style={s.card}>
                <table style={s.table}>
                  <thead>
                    <tr>{['Name', 'Email', 'Role', 'Joined', 'Actions'].map(h => <th key={h} style={s.th}>{h}</th>)}</tr>
                  </thead>
                  <tbody>
                    {users.map(u => (
                      <tr key={u.user_id} style={s.tr}>
                        <td style={{ ...s.td, fontWeight: 500, color: NAVY }}>{u.full_name || '—'}</td>
                        <td style={{ ...s.td, color: '#64748b', fontSize: 12 }}>{u.email}</td>
                        <td style={{ ...s.td, textTransform: 'capitalize' }}>{u.role}</td>
                        <td style={{ ...s.td, color: '#94a3b8', fontSize: 12 }}>{fmtDate(u.created_on_utc)}</td>
                        <td style={s.td}>
                          <button style={s.actionBtn} onClick={() => setResetUser(u)}>Reset Password</button>
                        </td>
                      </tr>
                    ))}
                    {users.length === 0 && (
                      <tr><td colSpan={5} style={{ ...s.td, color: '#94a3b8', textAlign: 'center', padding: 24 }}>No team members</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}

            {/* Create Discount tab */}
            {tab === 'create-discount' && (
              <div style={{ ...s.card, padding: 28, maxWidth: 560 }}>
                <h2 style={{ color: NAVY, fontSize: 16, fontWeight: 700, margin: '0 0 8px' }}>Create Discount Code</h2>
                <div style={s.infoBox}>
                  This discount will be locked to <strong>{tenant.company_name}</strong> only.
                </div>

                {discError  && <div style={s.errorBox}>{discError}</div>}
                {discSaved  && <div style={s.successBox}>Discount code created successfully!</div>}

                <form onSubmit={handleDiscountSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                  <div>
                    <label style={s.label}>Code</label>
                    <input style={s.input} value={disc.code}
                      onChange={e => setDisc(p => ({ ...p, code: e.target.value.toUpperCase() }))}
                      placeholder="e.g. ACME-10" required />
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                    <div>
                      <label style={s.label}>Type</label>
                      <select style={s.input} value={disc.type}
                        onChange={e => setDisc(p => ({ ...p, type: e.target.value }))}>
                        <option value="percent">Percent (%)</option>
                        <option value="fixed">Fixed ($)</option>
                      </select>
                    </div>
                    <div>
                      <label style={s.label}>Value</label>
                      <input style={s.input} type="number" min="0" step="any"
                        value={disc.value} onChange={e => setDisc(p => ({ ...p, value: e.target.value }))}
                        placeholder={disc.type === 'percent' ? '10' : '5.00'} required />
                    </div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                    <div>
                      <label style={s.label}>Max Uses <span style={{ color: '#94a3b8', fontWeight: 400 }}>(optional)</span></label>
                      <input style={s.input} type="number" min="1"
                        value={disc.max_uses} onChange={e => setDisc(p => ({ ...p, max_uses: e.target.value }))}
                        placeholder="Unlimited" />
                    </div>
                    <div>
                      <label style={s.label}>Expires <span style={{ color: '#94a3b8', fontWeight: 400 }}>(optional)</span></label>
                      <input style={s.input} type="date"
                        value={disc.expires} onChange={e => setDisc(p => ({ ...p, expires: e.target.value }))} />
                    </div>
                  </div>
                  <div>
                    <label style={s.label}>Notes <span style={{ color: '#94a3b8', fontWeight: 400 }}>(optional)</span></label>
                    <input style={s.input} value={disc.notes}
                      onChange={e => setDisc(p => ({ ...p, notes: e.target.value }))}
                      placeholder="Internal notes about this discount" />
                  </div>
                  <button type="submit" style={{ ...s.btnPrimary, marginTop: 4 }} disabled={discSaving}>
                    {discSaving ? 'Creating…' : 'Create Discount Code'}
                  </button>
                </form>
              </div>
            )}
          </>
        )}
      </main>

      {resetUser && <ResetPasswordModal user={resetUser} onClose={() => setResetUser(null)} />}
    </div>
  );
}

const s = {
  page:      { display: 'flex', minHeight: '100vh', fontFamily: "'Inter', system-ui, sans-serif" },
  main:      { flex: 1, background: '#f0f5fb', padding: '36px 40px', overflowY: 'auto' },
  header:    { marginBottom: 24 },
  backBtn:   { background: 'transparent', border: 'none', color: BLUE, cursor: 'pointer', fontSize: 13, fontWeight: 500, padding: '0 0 10px', display: 'block' },
  title:     { color: NAVY, fontSize: 26, fontWeight: 700, margin: 0, letterSpacing: '-0.4px' },
  sub:       { color: '#64748b', fontSize: 13, margin: '4px 0 0' },
  statsRow:  { display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 16, marginBottom: 24 },
  tabBar:    { display: 'flex', gap: 0, borderBottom: '1px solid #e2e8f0', marginBottom: 20 },
  tabBtn:    { background: 'transparent', border: 'none', cursor: 'pointer', fontSize: 13, padding: '10px 18px', transition: 'all 0.12s' },
  card:      { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' },
  table:     { width: '100%', borderCollapse: 'collapse' },
  th:        { color: '#94a3b8', fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', padding: '10px 16px', textAlign: 'left', background: '#f8fafc', borderBottom: '1px solid #e2e8f0' },
  tr:        { borderBottom: '1px solid #f1f5f9' },
  td:        { color: '#374151', fontSize: 13, padding: '12px 16px', verticalAlign: 'middle' },
  actionBtn: { background: 'transparent', border: `1px solid ${BLUE}`, borderRadius: 6, color: BLUE, cursor: 'pointer', fontSize: 11, fontWeight: 500, padding: '4px 10px' },
  dlBtn:     { display: 'inline-block', background: 'transparent', border: `1px solid ${BLUE}`, borderRadius: 5, color: BLUE, cursor: 'pointer', fontSize: 11, fontWeight: 500, padding: '3px 8px', textDecoration: 'none' },
  label:     { display: 'block', color: '#64748b', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 5 },
  input:     { background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, color: NAVY, fontSize: 14, padding: '10px 12px', outline: 'none', width: '100%', boxSizing: 'border-box' },
  infoBox:   { background: 'rgba(1,118,206,0.06)', border: '1px solid rgba(1,118,206,0.25)', borderRadius: 8, color: BLUE, fontSize: 13, padding: '10px 14px', marginBottom: 18 },
  errorBox:  { background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 8, color: '#dc2626', fontSize: 13, padding: '10px 14px', marginBottom: 16 },
  successBox:{ background: 'rgba(5,150,105,0.08)', border: '1px solid rgba(5,150,105,0.3)', borderRadius: 8, color: '#059669', fontSize: 13, padding: '10px 14px', marginBottom: 16 },
  overlay:   { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modal:     { background: '#fff', borderRadius: 16, padding: '36px 32px', width: '100%', maxWidth: 420, boxShadow: '0 24px 60px rgba(0,0,0,0.25)' },
  modalTitle:{ color: NAVY, fontSize: 18, fontWeight: 700, margin: '0 0 8px' },
  btnPrimary:{ background: ORANGE, border: 'none', borderRadius: 8, color: '#fff', cursor: 'pointer', fontSize: 14, fontWeight: 600, padding: '11px 20px', width: '100%' },
  btnGhost:  { background: 'transparent', border: '1px solid #e2e8f0', borderRadius: 8, color: '#64748b', cursor: 'pointer', fontSize: 14, fontWeight: 500, padding: '11px 20px' },
};
